import React, { useState, useEffect, useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import { fetchWorks, Work, workTitle } from '../services/work';
import { useContent } from '../contexts/ContentContext';
import Modal from './Modal';
import SongDetail from './SongDetail';
import { formatDateYmd, toEpochMs } from '../utils/time';
import { toWebpPath } from '../utils/image';
import ContentPageHeader from './PageHeader';
import { useRestoreListPosition } from './BackToListButton';

interface SongGalleryProps {
    platform?: string | null;
}

const SongGallery: React.FC<SongGalleryProps> = ({ platform }) => {
    const [songs, setSongs] = useState<Work[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);
    const [reload, setReload] = useState(0);
    useRestoreListPosition(!isLoading && !error);
    const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
    const { content, language, t } = useContent();
    const [selectedSong, setSelectedSong] = useState<Work | null>(null);
    const kind = new URLSearchParams(window.location.search).get('kind');
    const platformLabel = (platform: string) => content.ui[language].work.platform[platform as keyof typeof content.ui.jp.work.platform] || platform;
    const selectedPlatform = platform?.toLowerCase() || null;

    useEffect(() => {
        let active = true;
        setIsLoading(true);
        setError(false);
        fetchWorks().then(data => {
            if (active) setSongs([...data].sort((a, b) =>
                ((toEpochMs(b.publishedAt) ?? 0) - (toEpochMs(a.publishedAt) ?? 0)) || a.id.localeCompare(b.id)
            ));
        }).catch(() => {
            if (active) setError(true);
        }).finally(() => {
            if (active) setIsLoading(false);
        });
        return () => { active = false; };
    }, [reload]);


    const filteredSongs = useMemo(() => songs.filter(song => (!selectedPlatform || song.platform === selectedPlatform) && (!kind || song.kind === kind)), [songs, selectedPlatform, kind]);
    const detailUrl = (song: Work) => `/?page=gallery&tab=works&view=song&platform=${encodeURIComponent(song.platform || '')}&id=${encodeURIComponent(song.id)}`;

    return (
        <div className="lab-page lab-works lab-content-page">
            <ContentPageHeader sidebar count={!isLoading && !error ? t('workPage.count', { count: filteredSongs.length }) : undefined}
                description={t('workPage.lead')} />

            {isLoading ? <p role="status" className="py-12 text-center">{content.ui[language].common.loading}</p> : error ? (
                <div role="alert" className="p-8 rounded-lg border border-red-300">
                    <p>{t('workPage.error')}</p>
                    <button type="button" className="lab-button mt-3" onClick={() => setReload(value => value + 1)}>{t('common.retry')}</button>
                </div>
            ) : filteredSongs.length === 0 ? (
                <div className="p-8 rounded-lg border border-gray-300 dark:border-gray-600 ivory:border-coffee-200">
                    <p>{t('workPage.empty')}</p>
                    <a href="/?page=gallery&tab=works" className="lab-button mt-3">{t('workPage.allWorks')}</a>
                </div>
            ) : (
                <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))' }}>
                    {filteredSongs.map(song => (
                        <article key={song.id} className="min-w-0 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white dark:bg-gray-800 ivory:bg-coffee-100 shadow-sm">
                            <a href={detailUrl(song)} onClick={event => {
                                if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
                                event.preventDefault();
                                setSelectedSong(song);
                            }} className="group flex flex-col h-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:-outline-offset-2">
                                <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-700 ivory:bg-coffee-200 flex items-center justify-center overflow-hidden">
                                    {song.thumbnail && !failedImages.has(song.id) ? (
                                        <img src={toWebpPath(song.thumbnail)} alt={workTitle(song, t('workPage.untitled'))} loading="lazy" className="w-full h-full object-contain" onError={() => setFailedImages(current => new Set(current).add(song.id))} />
                                    ) : <span className="text-xl text-gray-500 dark:text-gray-300 ivory:text-coffee-700">{t('workPage.noImage')}</span>}
                                </div>
                                <div className="p-5 flex flex-col flex-1 gap-3">
                                    <div className="flex flex-wrap gap-2 text-xs break-all">
                                        <span className="rounded bg-gray-100 dark:bg-gray-700 ivory:bg-coffee-200 px-2 py-1 capitalize">{platformLabel(song.platform)}</span>
                                        <span className="rounded bg-blue-50 dark:bg-blue-900 ivory:bg-coffee-200 px-2 py-1">{t(`work.kind.${song.kind}`)}</span><span>{t(`work.format.${song.format}`)}</span>
                                    </div>
                                    <h2 className="font-bold text-lg break-words group-hover:underline">{workTitle(song, t('workPage.untitled'))}</h2>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 ivory:text-coffee-600 break-words">{song.description || (t('workPage.noDescription'))}</p>
                                    <div className="mt-auto pt-2 flex flex-wrap justify-between gap-2 text-sm">
                                        <span className="text-gray-500 dark:text-gray-400 ivory:text-coffee-600">{formatDateYmd(song.publishedAt) || (t('workPage.noDate'))}</span>
                                        <span className="inline-flex items-center gap-1 font-semibold text-blue-700 dark:text-blue-300 ivory:text-coffee-700">{t('workPage.open')}<ArrowRight size={16} aria-hidden="true" className="shrink-0" /></span>
                                    </div>
                                </div>
                            </a>
                        </article>
                    ))}
                </div>
            )}

            <Modal helpSection="work-detail" isOpen={!!selectedSong} onClose={() => setSelectedSong(null)} title={selectedSong ? workTitle(selectedSong, t('workPage.untitled')) : undefined}>
                {selectedSong && <>
                    <a href={detailUrl(selectedSong)} className="inline-block mb-4 underline">{t('workPage.detail')}</a>
                    <SongDetail work={selectedSong} isModal={true} />
                </>}
            </Modal>
        </div>
    );
};

export default SongGallery;
